import { useState, useEffect, useRef } from 'react';
import { logDiagnostics } from '../utils/diagnostics';

// Production defaults - customers can override if needed
const DEFAULT_VALIDATION_ENDPOINT = 'https://tzowmzqfbicbyvikhdwy.supabase.co/functions/v1/validate-mockdetector-key';
const DEFAULT_HEARTBEAT_ENDPOINT = 'https://tzowmzqfbicbyvikhdwy.supabase.co/functions/v1/widget-heartbeat';
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Automatically synced from package.json during build
declare const __WIDGET_VERSION__: string;
const CURRENT_WIDGET_VERSION = __WIDGET_VERSION__;

interface ApiKeyValidationResult {
  isValid: boolean | null;
  validationError: string | null;
  dailyScanLimit: number;
  isLoading: boolean;
  keyData: {
    tier: string;
    domain: string | null;
    scansUsedToday: number;
  } | null;
}

export const useApiKeyValidation = (
  apiKey?: string,
  enableHeartbeat = true,
  validationEndpoint = DEFAULT_VALIDATION_ENDPOINT,
  heartbeatEndpoint = DEFAULT_HEARTBEAT_ENDPOINT
): ApiKeyValidationResult => {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dailyScanLimit, setDailyScanLimit] = useState<number>(50); // Default free tier limit
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyData, setKeyData] = useState<ApiKeyValidationResult['keyData']>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Validate API key on mount and when it changes
  useEffect(() => {
    if (!apiKey) {
      setIsValid(null);
      setValidationError(null);
      setDailyScanLimit(50); // Default limit for local dev
      setKeyData(null);
      return;
    }

    const validateApiKey = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(validationEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            apiKey,
            domain: window.location.hostname
          })
        });

        const result = await response.json();

        if (result.valid) {
          setIsValid(true);
          setValidationError(null);
          setDailyScanLimit(result.dailyScanLimit || 60);
          setKeyData({
            tier: result.tier || 'free',
            domain: result.domain || null,
            scansUsedToday: result.scansUsedToday || 0
          });
        } else {
          const errorMessage = result.error || 'Invalid API key';
          setIsValid(false);
          setValidationError(errorMessage);
          setDailyScanLimit(0);
          setKeyData(null);
          
          // Log diagnostic information for debugging
          logDiagnostics(errorMessage);
        }
      } catch (error) {
        console.error('[MockDetector] API key validation failed:', error);
        const errorMessage = 'Failed to validate API key';
        setIsValid(false);
        setValidationError(errorMessage);
        setDailyScanLimit(0);
        setKeyData(null);
        
        // Log diagnostic information
        logDiagnostics(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    validateApiKey();
  }, [apiKey]);

  // Send heartbeat every 5 minutes
  useEffect(() => {
    if (!apiKey || !enableHeartbeat || !isValid) {
      // Clear interval if conditions not met
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    const sendHeartbeat = async () => {
      try {
        await fetch(heartbeatEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            apiKey,
            domain: window.location.hostname,
            widgetVersion: CURRENT_WIDGET_VERSION
          })
        });
      } catch (error) {
        console.error('[MockDetector] Heartbeat failed:', error);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval for recurring heartbeats
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [apiKey, enableHeartbeat, isValid]);

  return {
    isValid,
    validationError,
    dailyScanLimit,
    isLoading,
    keyData
  };
};
